import AddressForm from 'features/common/form/address';
import React, { useState, useEffect, useRef } from 'react';
import SlideOutSidebar from 'ui-components/slide-out-sidebar/slideOutSidebar';
import ExecutiveContactDetails from 'features/common/form/executiveContactDetails';
import { TerminalState } from 'slices/terminal-summary/TerminalDetailsState';
import { useDispatch, useSelector } from 'react-redux';
import { createNewTerminal, resetTerminalDetails } from 'slices/terminal-summary/terminalDetailsSlice';
import { terminalDetailsSelector } from 'slices/terminal-summary/terminalDetailsSlice';
import { AddTerminal, validationSchema } from 'app/data/terminal-details/models';
import Contact from 'features/common/form/contact';
import accordionOpen from "images/arrow_up_black.svg";
import HoursOfOperationForm from 'features/common/form/hoursOfOperations';
import { Form, Formik, FormikProps } from 'formik';
import ActionSection from 'features/terminal-details/common/actionSection';
import './addEditTerminal.scss';
import BasicInfo from './basicInfo';
import { Region } from 'features/common/form/region';
import _ from 'lodash';
import { updateStatusSelector } from 'slices/terminal-summary/terminalStatusSlice';
import { toast } from 'react-toastify';
import AgentAddressDetails from 'features/common/form/agentAddressDetails';
import XGSToggle from 'ui-components/xgs-toggle/xgs-toggle';

interface AccordionProps {
    isEdit: boolean;
    slideOpen: boolean;
    slideClose: () => void;
    handleSuccess?: () => void;
}

const AddEditTerminal: React.FC<AccordionProps> = ({ isEdit, slideOpen, slideClose, handleSuccess }) => {
    const [openSections, setOpenSections] = useState<number[]>([]);
    const [isMailingAddressSameAsPhysical, setIsMailingAddressSameAsPhysical] = useState<boolean>(false);

    const [spinner, setSpinner] = useState<boolean>(false);
    const [activeTerminal, setActiveTerminal] = useState<boolean>(isEdit ? false : true);
    const terminalState: TerminalState = useSelector(terminalDetailsSelector);
    const updateStatusState = useSelector(updateStatusSelector);
    const dispatch = useDispatch();
    const formRef = useRef<FormikProps<AddTerminal>>(null);
    const sidebarContentRef = useRef<HTMLDivElement>(null);

    const initialValues: AddTerminal = {
        terminalNumber: terminalState?.data?.terminalNumber?.toString() || "",
        terminalType: terminalState?.data?.terminalType?.type || "",
        division: terminalState?.data?.division || "",
        timezone: terminalState?.data?.timezone || "",
        airportCode: terminalState?.data?.airportCode || "",
        status: terminalState?.data?.status || "",
        terminalName: terminalState?.data?.terminalName || "",
        wmsWarehousing: terminalState.data ? (terminalState?.data?.warehouse ? "Yes" : "No") : "",
        phoneNumber: terminalState?.data?.contact?.phoneNumber?.formatPhone() || "",
        street: terminalState?.data?.address?.physicalAddress || "",
        city: terminalState?.data?.address?.city || "",
        state: terminalState?.data?.address?.stateProvince || "",
        zip: terminalState?.data?.address?.zipPostalCode || "",
        country: terminalState?.data?.address?.country || "",
        mailingAddress: terminalState?.data?.address?.mailingAddress || "",
        mailingCity: terminalState?.data?.address?.mailingCity || "",
        mailingStateProvince: terminalState?.data?.address?.mailingStateProvince || "",
        mailingZipPostalCode: terminalState?.data?.address?.mailingZipPostalCode || "",
        mailingCountry: terminalState?.data?.address?.mailingCountry || "",
        isMailingAddressSame: terminalState?.data?.address?.isMailingAddressSame || false,
        managerName: terminalState?.data?.contact?.managerName || "",
        managerPhone: terminalState?.data?.contact?.managerPhone?.formatPhone() || "",
        managerEmail: terminalState?.data?.contact?.managerEmail || "",
        agentName: terminalState?.data?.contact?.agentName || "",
        agentPhone: terminalState?.data?.contact?.agentPhone?.formatPhone() || "",
        agentEmail: terminalState?.data?.contact?.agentEmail || "",
        agentAddress: terminalState?.data?.contact?.agentAddress || "",
        agCity: terminalState?.data?.contact?.agCity || "",
        agState: terminalState?.data?.contact?.agState || "",
        vpName: terminalState?.data?.region?.vpName || "",
        vpPhone: terminalState?.data?.region?.vpPhone?.formatPhone() || "",
        vpEmail: terminalState?.data?.region?.vpEmail || "",
        regionName: terminalState?.data?.region?.regionName || "",
        directorName: terminalState?.data?.region?.directorName || "",
        directorPhone: terminalState?.data?.region?.directorPhone?.formatPhone() || "",
        directorEmail: terminalState?.data?.region?.directorEmail || "",
        terminalEmail: terminalState?.data?.contact?.terminalEmail || "",
        operationOpenHours: terminalState.data?.terminalOpenHoursList.filter((item: any) => item.type === "OPERATION").map((item: any) => item.openHours).join(", ") || 'Monday-Friday: --:-- - --:--',
        willCallOpenHours: terminalState.data?.terminalOpenHoursList.filter((item: any) => item.type === "WILL_CALL").map((item: any) => item.openHours).join(", ") || 'Monday-Friday: --:-- - --:--',
        operationFromTime: terminalState.data?.terminalOpenHoursList?.filter((item) => item.type === "OPERATION")?.map((item: any) => item.fromTime).join(", ") || null,
        operationToTime: terminalState.data?.terminalOpenHoursList?.filter((item) => item.type === "OPERATION")?.map((item: any) => item.toTime).join(", ") || null,
        willCallFromTime: terminalState.data?.terminalOpenHoursList?.filter((item) => item.type === "WILL_CALL")?.map((item: any) => item.fromTime).join(", ") || null,
        willCallToTime: terminalState.data?.terminalOpenHoursList?.filter((item) => item.type === "WILL_CALL")?.map((item: any) => item.toTime).join(", ") || null,
    };

    const sections = [
        {
            id: 1,
            title: 'General Information',
            content:
                <BasicInfo />
        },
        {
            id: 2,
            title: 'Address Information',
            content:
                <>
                    <AddressForm />
                    <div className="form-checkbox">
                        <label>
                            <input
                                type="checkbox"
                                className="form-checkbox__input"
                                checked={isMailingAddressSameAsPhysical}
                                onChange={() => {
                                    const checked = !isMailingAddressSameAsPhysical;
                                    setIsMailingAddressSameAsPhysical(checked);
                                    formRef.current?.setFieldTouched("mailingAddress", false);
                                    formRef.current?.setFieldTouched("mailingCity", false);
                                    formRef.current?.setFieldTouched("mailingStateProvince", false);
                                    formRef.current?.setFieldTouched("mailingZipPostalCode", false);
                                    formRef.current?.setFieldTouched("mailingCountry", false);
                                    if (checked) {
                                        formRef.current?.setValues({
                                            ...formRef.current?.values,
                                            mailingAddress: formRef.current?.values.street || "",
                                            mailingCity: formRef.current?.values.city || "",
                                            mailingStateProvince: formRef.current?.values.state || "",
                                            mailingZipPostalCode: formRef.current?.values.zip || "",
                                            mailingCountry: formRef.current?.values.country || "",
                                            isMailingAddressSame: true
                                        });
                                    }
                                    else {
                                        formRef.current?.setValues({
                                            ...formRef.current?.values,
                                            mailingAddress: "",
                                            mailingCity: "",
                                            mailingStateProvince: "",
                                            mailingZipPostalCode: "",
                                            mailingCountry: "",
                                            isMailingAddressSame: false
                                        });
                                    }
                                }}
                            />
                            {" "}
                        </label>
                        <div>Mailing address same as physical</div>
                    </div>
                    <div className="form-heading">Mailing Address</div>
                    <AddressForm isSameAsPhysical={isMailingAddressSameAsPhysical} isMailingAddress={true} />
                </>
        },
        {
            id: 3,
            title: 'Contact Information',
            content:
                <>
                    <div className=" margin-bottom-28">
                        <Contact/>
                    </div>
                    <div className="margin-vertical-20 sub-heading">Manager Details</div>
                    <ExecutiveContactDetails
                        executive="manager"
                        label='Manager'
                        required={true}
                    />
                    <div className="margin-vertical-20 sub-heading">Agent Details</div>
                    <ExecutiveContactDetails
                        executive="agent"
                        label='Agent'
                        required={false}
                    />
                    <div className="margin-vertical-20">
                        <AgentAddressDetails />
                    </div>

                </>
        },
        {
            id: 4,
            title: 'Region Information',
            content:
                <>
                    <div className="margin-bottom-28">
                        <Region />
                    </div>
                    <div className="margin-vertical-20 sub-heading">Vice President</div>
                    <ExecutiveContactDetails
                        executive="vp"
                        label='Vice President'
                        required={false}
                    />

                    <div className="margin-vertical-20 sub-heading">Director</div>
                    <ExecutiveContactDetails
                        executive="director"
                        label='Director'
                        required={false}
                    />

                </>
        },
        {
            id: 5,
            title: 'Hours of operation',
            content:
                <HoursOfOperationForm />
        },
    ];

    useEffect(() => {
        if (!isEdit) {
            // Open all sections
            setOpenSections(sections.map((s) => s.id));
            dispatch(resetTerminalDetails());

        } else {
            // Only the first section open by default
            setOpenSections([sections[0].id]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, dispatch]);

    useEffect(() => {
        if (isEdit) {
            setActiveTerminal(terminalState.data?.status === "ACTIVE");
        }
    }, [terminalState.data, isEdit]);

    const handleActive = () => {
            handleSpinner();
            return;
    }

    const toggleSection = (id: number) => {
        if (isEdit) {
            // Only one open at a time
            setOpenSections([id]);
        } else {
            // Toggle individually
            setOpenSections((prev) =>
                prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
            );
        }
    };

    const handleSpinner = () => {
        setTimeout(() => {
            setSpinner(false);
        }, 1000);
    };




    return (
        <SlideOutSidebar show={slideOpen} onClose={() => slideClose()} header={isEdit ? "Edit Terminal" : "Add Terminal"} size="small">
            <Formik
                onSubmit={() => { }}
                initialValues={initialValues}
                validationSchema={validationSchema}
                innerRef={formRef}
                enableReinitialize
            >
                {(formikProps: FormikProps<AddTerminal>) => (
                    <Form>
                        <div ref={sidebarContentRef} className={`scroll-up`}>
                            {sections.map(({ id, title, content }) => (
                                <div key={id} id={isEdit ? "edit-" + id.toString() : "add-" + id.toString()} className="accordion">
                                    <div onClick={() => toggleSection(id)} className="section-header">
                                        <div style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                            {title}
                                        </div>
                                        <div style={{ cursor: 'pointer' }}>
                                            {openSections.includes(id) ? (
                                                <img src={accordionOpen} alt="arrowUp" />
                                            ) : (
                                                <img src={accordionOpen} style={{ transform: 'rotate(180deg)' }} alt="arrowDown" />
                                            )}
                                        </div>
                                    </div>
                                    {/* Always render content but hide with CSS when closed */}

                                    <div
                                        style={{
                                            display: openSections.includes(id) ? 'block' : 'none',
                                            padding: openSections.includes(id) ? '10px' : '0',
                                        }}
                                    >
                                        {content}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="action-section__toggle">
                            <XGSToggle
                                onChange={() => {
                                    setSpinner(true);
                                    setActiveTerminal(!activeTerminal);
                                    handleActive();
                                }}
                                checked={activeTerminal}
                                label="Active"
                            />
                            <ActionSection
                                handleClear={() => {
                                    formRef.current?.resetForm();
                                }}
                                spinner={terminalState?.isLoading || updateStatusState.isLoading || spinner}
                                handleSave={async () => {
                                    setSpinner(true);
                                    await Promise.all([
                                        formRef.current?.submitForm(),
                                    ]);

                                    const isValid = _.isEmpty(formRef?.current?.errors)

                                    if (!isValid) {
                                        handleSpinner();
                                        setTimeout(() => {
                                            // Scroll the sidebar content to top;
                                            if (sidebarContentRef.current) {
                                                const element = document.getElementById(`add-${sections[0].id}`)
                                                if (element) {
                                                    element?.scrollIntoView({ behavior: "smooth", block: "start" });
                                                }
                                            }
                                        }, 1000);
                                        return;
                                    }
                                    if (isValid) {
                                        handleSpinner();
                                        dispatch(createNewTerminal(
                                            {
                                                terminalName: formRef?.current?.values?.terminalName || null,
                                                airportCode: formRef?.current?.values?.airportCode || null,
                                                status: activeTerminal ? "ACTIVE" : "INACTIVE",
                                                division: formRef?.current?.values?.division || null,
                                                warehouse: formRef?.current?.values?.wmsWarehousing === "Yes",
                                                timezone: formRef?.current?.values?.timezone || null,
                                                terminalType: formRef?.current?.values?.terminalType || null,
                                                operationDays: "Monday-Friday",
                                                operationFromTime: formRef?.current?.values?.operationFromTime || null,
                                                operationToTime: formRef?.current?.values?.operationToTime || null,
                                                willCallDays: "Monday-Friday",
                                                willCallFromTime: formRef?.current?.values?.willCallFromTime || null,
                                                willCallToTime: formRef?.current?.values?.willCallToTime || null,
                                                address: {
                                                    physicalAddress: formRef?.current?.values?.street || null,
                                                    city: formRef?.current?.values?.city || null,
                                                    stateProvince: formRef?.current?.values?.state || null,
                                                    zipPostalCode: formRef?.current?.values?.zip || null,
                                                    country: formRef?.current?.values?.country || null,
                                                    isMailingAddressSame: isMailingAddressSameAsPhysical,
                                                    mailingAddress: formRef?.current?.values?.mailingAddress || null,
                                                    mailingCity: formRef?.current?.values?.mailingCity || null,
                                                    mailingStateProvince: formRef?.current?.values?.mailingStateProvince || null,
                                                    mailingZipPostalCode: formRef?.current?.values?.mailingZipPostalCode || null,
                                                    mailingCountry: formRef?.current?.values?.mailingCountry || null
                                                },
                                                contact: {
                                                    phoneNumber: formRef?.current?.values?.phoneNumber || null,
                                                    terminalEmail: formRef?.current?.values?.terminalEmail || null,
                                                    managerName: formRef?.current?.values?.managerName || null,
                                                    managerEmail: formRef?.current?.values?.managerEmail || null,
                                                    managerPhone: formRef?.current?.values?.managerPhone || null,
                                                    agentName: formRef?.current?.values?.agentName || null,
                                                    agentEmail: formRef?.current?.values?.agentEmail || null,
                                                    agentPhone: formRef?.current?.values?.agentPhone || null,
                                                    agentAddress: formRef?.current?.values?.agentAddress || null,
                                                    agCity: formRef?.current?.values?.agCity || null,
                                                    agState: formRef?.current?.values?.agState || null,
                                                },
                                                region: {
                                                    regionName: formRef?.current?.values?.regionName || null,
                                                    vpName: formRef?.current?.values?.vpName || null,
                                                    vpPhone: formRef?.current?.values?.vpPhone || null,
                                                    vpEmail: formRef?.current?.values?.vpEmail || null,
                                                    directorName: formRef?.current?.values?.directorName || null,
                                                    directorPhone: formRef?.current?.values?.directorPhone || null,
                                                    directorEmail: formRef?.current?.values?.directorEmail || null
                                                },
                                            }
                                            , handleSuccess, ()=>{
                                               toast.error("Error! Please try again later...") 
                                            }))
                                    }

                                }}
                                primaryButtonText={"Create Terminal"}
                                secondaryButtonText={"Cancel"}
                            />
                        </div>
                    </Form>)}
            </Formik>

        </SlideOutSidebar>
    );
};

export default AddEditTerminal;